import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { TRY, NUM, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Undo2, Loader2, Trash2, RotateCcw, AlertTriangle, ArrowRight } from "lucide-react";
import { lineDiscount } from "@/components/QnbIncomingLines";
import { toast } from "sonner";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Gelen faturadan İADE faturası: kalemleri seç / miktar düzenle → taslak oluştur → QNB kesim diyaloğuna geç (önizleme + kes). */
export default function ReturnInvoiceDialog({ doc, onClose, onCreated }) {
  const lines = useMemo(() => doc?.parsed?.lines || [], [doc]);
  const [sel, setSel] = useState({});   // index → quantity
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!doc) return;
    const all = {};
    lines.forEach((ln, i) => { all[i] = Number(ln.quantity || 0); });
    setSel(all); setNote("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc?.id]);

  const rows = useMemo(() => lines.map((ln, i) => {
    const x = lineDiscount(ln);
    const qty = sel[i] != null ? Number(sel[i]) : 0;
    const net = r2(qty * x.netUnit);
    const tax = r2(net * Number(ln.tax_rate || 0) / 100);
    return { i, ln, x, qty, net, tax, total: r2(net + tax), on: sel[i] != null };
  }), [lines, sel]);
  const chosen = rows.filter((r) => r.on);
  const totals = chosen.reduce((a, r) => ({ net: a.net + r.net, tax: a.tax + r.tax, total: a.total + r.total }), { net: 0, tax: 0, total: 0 });
  const invalid = chosen.some((r) => !(r.qty > 0) || r.qty > Number(r.ln.quantity || 0) + 1e-6);

  const toggle = (i) => setSel((s) => { const n = { ...s }; if (n[i] != null) delete n[i]; else n[i] = Number(lines[i].quantity || 0); return n; });
  const remove = (i) => setSel((s) => { const n = { ...s }; delete n[i]; return n; });
  const setQty = (i, v) => setSel((s) => ({ ...s, [i]: v === "" ? "" : Number(v) }));
  const keepOnly = (i) => setSel({ [i]: Number(lines[i].quantity || 0) });
  const reset = () => { const all = {}; lines.forEach((ln, i) => { all[i] = Number(ln.quantity || 0); }); setSel(all); };

  const create = async () => {
    if (!chosen.length) return toast.error("İade edilecek en az bir kalem bırakın");
    if (invalid) return toast.error("İade miktarı 0'dan büyük ve fatura miktarını aşmamalı");
    setBusy(true);
    try {
      const { data } = await api.post(`/qnb/incoming/${doc.id}/return-draft`, { lines: chosen.map((r) => ({ index: r.i, quantity: r.qty })), note });
      toast.success(`İade faturası taslağı oluşturuldu · ${TRY(data.invoice.total)} — önizleyip kesebilirsiniz`);
      onCreated && onCreated(data.invoice);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-4xl max-h-[94vh] overflow-y-auto p-0 gap-0" data-testid="return-invoice-dialog">
        <DialogHeader className="px-5 py-3 bg-gradient-to-br from-orange-600 to-orange-500 text-white rounded-t-lg">
          <DialogTitle className="font-head text-xl flex items-center gap-2 text-white">
            <span className="h-8 w-8 rounded-xl bg-white/15 flex items-center justify-center"><Undo2 className="h-4 w-4" /></span>
            İade Faturası Kes
            {doc && <span className="ml-auto font-sans text-xs font-normal text-white/85 hidden sm:flex items-center gap-2">
              <span className="font-semibold text-white truncate max-w-[260px]">{doc.sender_name}</span><span>•</span>
              <span className="font-mono">{doc.belge_no}</span><span>•</span><span>{fmtDate(doc.belge_tarihi)}</span>
            </span>}
          </DialogTitle>
        </DialogHeader>
        {doc && (
          <div className="p-4 space-y-3">
            <div className="rounded-xl border border-orange-200 bg-orange-50 px-3.5 py-2.5 text-xs text-orange-950 flex gap-2" data-testid="return-hint">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-orange-600" />
              <div>Faturadaki kalemler geldi. <b>İade edilecek ürünü bırakın, diğerlerini silin</b>; gerekirse miktarı azaltın. Fiyat ve iskonto orijinal faturadaki gibidir. Sonraki adımda GİB görünümünü <b>önizleyip</b> <b>{doc.sender_name}</b> firmasına İADE faturası kesersiniz.</div>
            </div>
            <div className="rounded-xl border overflow-hidden" data-testid="return-lines">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-white text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 w-8" />
                    <th className="px-3 py-2 text-left">Mal / Hizmet</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Faturadaki</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap w-32">İade Miktarı</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">Net Birim</th>
                    <th className="px-3 py-2 text-right whitespace-nowrap">İade Tutarı</th>
                    <th className="px-3 py-2 w-24" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.i} className={`border-b last:border-0 ${r.on ? "bg-white" : "bg-slate-50 opacity-50"}`} data-testid={`return-line-${r.i}`}>
                      <td className="px-3 py-2"><Checkbox checked={r.on} onCheckedChange={() => toggle(r.i)} data-testid={`return-line-check-${r.i}`} /></td>
                      <td className="px-3 py-2 min-w-[200px]">
                        <div className="font-semibold leading-snug">{r.ln.name}</div>
                        <div className="text-[11px] text-muted-foreground">{[r.ln.code && `Kod ${r.ln.code}`, r.ln.description].filter(Boolean).join(" · ")}{r.x.rate > 0 && <Badge variant="outline" className="ml-1 text-[10px] py-0 border-primary/40 text-primary">isk. %{NUM(r.x.rate)}</Badge>}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{NUM(r.ln.quantity)} <span className="text-[11px] text-muted-foreground">{r.ln.unit}</span></td>
                      <td className="px-3 py-2 text-right">
                        <Input type="number" min="0" step="any" value={r.on ? sel[r.i] : ""} disabled={!r.on} onChange={(e) => setQty(r.i, e.target.value)} className={`h-8 text-right tabular-nums ${r.on && (!(r.qty > 0) || r.qty > Number(r.ln.quantity || 0) + 1e-6) ? "border-red-400" : ""}`} data-testid={`return-line-qty-${r.i}`} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{TRY(r.x.netUnit)}<div className="text-[10px] text-muted-foreground">+KDV %{NUM(r.ln.tax_rate)}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-head font-black">{r.on ? TRY(r.total) : "—"}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Yalnızca bu kalem kalsın" onClick={() => keepOnly(r.i)} data-testid={`return-line-only-${r.i}`}><ArrowRight className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Kalemi çıkar" onClick={() => remove(r.i)} disabled={!r.on} data-testid={`return-line-remove-${r.i}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground text-sm">Kalem bilgisi yok (UBL ayrıştırılamadı).</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="İade nedeni / not (faturaya yazılır, opsiyonel) — ör. yanlış ürün gönderildi" className="flex-1 text-sm" data-testid="return-note" />
              <div className="rounded-xl bg-[#0B192C] text-white px-4 py-3 min-w-[240px]" data-testid="return-totals">
                <div className="flex justify-between text-xs text-slate-300"><span>{chosen.length} kalem · Net</span><span className="tabular-nums">{TRY(totals.net)}</span></div>
                <div className="flex justify-between text-xs text-slate-300 mt-0.5"><span>KDV</span><span className="tabular-nums">{TRY(totals.tax)}</span></div>
                <div className="flex justify-between items-center mt-1.5 border-t border-white/10 pt-1.5"><span className="text-[11px] font-bold uppercase tracking-wider text-orange-300">İade Toplamı</span><span className="font-head font-black text-xl tabular-nums" data-testid="return-total">{TRY(totals.total)}</span></div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="px-4 pb-4 gap-2 flex-row items-center">
          <Button variant="ghost" size="sm" onClick={reset} disabled={busy} data-testid="return-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" />Tümünü geri getir</Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onClose && onClose()} disabled={busy} data-testid="return-cancel">Vazgeç</Button>
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={create} disabled={busy || !chosen.length || invalid} data-testid="return-create">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1.5" />}Taslağı Oluştur → Önizle & Kes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
